import React, { Component, ErrorInfo, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { AlertTriangle, Home, RefreshCw, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
    Sentry.captureException(error, { extra: { errorInfo } });
  }

  public render() {
    if (this.state.hasError) {
      const { error } = this.state;
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-2xl w-full text-center space-y-6">
            <div className="mx-auto w-24 h-24 bg-destructive/10 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-12 h-12 text-destructive" />
            </div>

            <div className="space-y-2">
              <h1 className="text-3xl font-bold tracking-tight">Algo salió mal</h1>
              <p className="text-muted-foreground">
                Ha ocurrido un error inesperado. Por favor, intenta de nuevo o regresa al inicio.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
              <Button
                variant="default"
                className="gap-2"
                onClick={() => window.location.reload()}
              >
                <RefreshCw className="w-4 h-4" />
                Recargar página
              </Button>
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => {
                  this.setState({ hasError: false });
                  const hasAuth = Object.keys(localStorage).some(key => key.includes("auth-token"));
                  window.location.href = (window.location.pathname.startsWith("/app") || hasAuth) ? "/app" : "/";
                }}
              >
                <Home className="w-4 h-4" />
                Volver al inicio
              </Button>
              {error && (
                <Button
                  variant="secondary"
                  className="gap-2"
                  onClick={() => {
                    const whatsappLink = `https://wa.me/525564888036?text=Hola,%20necesito%20ayuda%20con%20un%20error%20en%20RepasoGPC`;
                    window.open(whatsappLink, '_blank', 'noopener,noreferrer');
                  }}
                >
                  <MessageCircle className="w-4 h-4" />
                  Contactar Soporte
                </Button>
              )}
            </div>


          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
